import { Table, TableColumn, Badge, Avatar } from '../ui';
import { Prospect, Stage, ProspectStage } from './DealCard';
import AdsBadge from './AdsBadge';
import { formatMoney, daysInStage } from './meta';
import useMediaQuery, { PHONE_QUERY } from '../clients/useMediaQuery';

const STAGE_FALLBACK_NAMES: Record<ProspectStage, string> = {
  leads: 'Leads',
  qualified: 'Qualified',
  proposal: 'Proposal',
  closed_won: 'Closed Won',
  closed_lost: 'Closed Lost',
};

interface SalesListViewProps {
  prospects: Prospect[];
  stages: Stage[];
  onEdit: (prospect: Prospect) => void;
}

// Columns a phone drops: the board and the edit drawer still carry them.
const PHONE_HIDDEN = new Set(['source', 'stage_entered_at', 'assigned_to_name']);

export default function SalesListView({ prospects, stages, onEdit }: SalesListViewProps) {
  const phone = useMediaQuery(PHONE_QUERY);
  if (prospects.length === 0) {
    return (
      <div
        style={{
          background: 'var(--surface-card)',
          border: '1px solid var(--border-hairline)',
          borderRadius: 'var(--radius-lg)',
          padding: 'var(--space-10)',
          textAlign: 'center',
          fontSize: 'var(--text-sm)',
          color: 'var(--text-faint)',
        }}
      >
        No deals found.
      </div>
    );
  }

  const stageName = (id: ProspectStage) =>
    stages.find((s) => s.id === id)?.name || STAGE_FALLBACK_NAMES[id] || id;

  const columns: TableColumn<Prospect>[] = [
    {
      key: 'company_name',
      label: 'Company',
      wrap: phone,
      render: (value: string, row: Prospect) => (
        <span>
          <span
            style={{
              display: 'block',
              fontWeight: 'var(--weight-medium)' as React.CSSProperties['fontWeight'],
            }}
          >
            {value}
          </span>
          <span style={{ display: 'block', fontSize: 'var(--text-xs)', color: 'var(--text-faint)' }}>
            {row.contact_name}
          </span>
        </span>
      ),
    },
    {
      key: 'stage',
      label: 'Stage',
      render: (value: ProspectStage) => (
        <Badge
          tone={value === 'closed_won' ? 'up' : value === 'closed_lost' ? 'down' : 'neutral'}
          dot={value === 'closed_won'}
        >
          {stageName(value)}
        </Badge>
      ),
    },
    {
      key: 'source',
      label: 'Source',
      render: (_value: unknown, row: Prospect) => (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
          <AdsBadge prospect={row} />
          <span style={{ color: row.source ? 'var(--text-muted)' : 'var(--text-faint)', fontSize: 'var(--text-sm)' }}>
            {row.source || '—'}
          </span>
        </span>
      ),
    },
    {
      key: 'deal_value',
      label: 'Value',
      align: 'right',
      mono: true,
      render: (value: number) => formatMoney(value),
    },
    {
      key: 'stage_entered_at',
      label: 'In Stage',
      align: 'right',
      mono: true,
      render: (value: string) => daysInStage(value),
    },
    {
      key: 'assigned_to_name',
      label: 'Owner',
      align: 'center',
      render: (value?: string) =>
        value ? (
          <Avatar name={value} size={22} style={{ verticalAlign: 'middle' }} />
        ) : (
          <span style={{ color: 'var(--text-faint)' }}>—</span>
        ),
    },
  ];

  return (
    <Table
      columns={phone ? columns.filter((c) => !PHONE_HIDDEN.has(c.key)) : columns}
      rows={prospects}
      onRowClick={(row) => onEdit(row)}
      style={{ overflowX: 'auto' }}
    />
  );
}
